import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import PageHero from "@/components/shared/PageHero";

const PrivacyPolicy = () => {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      
      <PageHero
        title="Privacy Policy"
        subtitle="Legal"
        description="Your privacy is important to us. This policy outlines how we collect, use, and protect your personal information."
        variant="vibrant"
      />

      <section className="py-16">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto prose prose-invert">
            
            {/* Last Updated */}
            <p className="text-muted-foreground text-sm mb-8">
              Last Updated: February 2026
            </p>

            {/* Introduction */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-4">1. Introduction</h2>
              <div className="text-muted-foreground space-y-4">
                <p>
                  SDC Gulf ("we," "our," or "us") is committed to protecting your privacy. This Privacy Policy 
                  explains how we collect, use, disclose, and safeguard your information when you visit our 
                  website or use our services.
                </p>
                <p>
                  By accessing or using our services, you agree to the collection and use of information in 
                  accordance with this policy.
                </p>
              </div>
            </div>

            {/* Information Collection */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-4">2. Information We Collect</h2>
              <div className="text-muted-foreground space-y-4">
                <p><strong className="text-foreground">Personal Information:</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Name and contact information (email, phone number, address)</li>
                  <li>Company name and job title</li>
                  <li>Information provided through contact forms and inquiries</li>
                  <li>Communication history and correspondence</li>
                </ul>
                <p className="mt-4"><strong className="text-foreground">Automatically Collected Information:</strong></p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>IP address and browser type</li>
                  <li>Device information and operating system</li>
                  <li>Pages visited and time spent on our website</li>
                  <li>Referring website addresses</li>
                </ul>
              </div>
            </div>

            {/* How We Use Information */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-4">3. How We Use Your Information</h2>
              <div className="text-muted-foreground space-y-4">
                <p>We use the collected information for various purposes:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>To provide and maintain our services</li>
                  <li>To respond to your inquiries and provide customer support</li>
                  <li>To send you updates, marketing communications, and promotional materials</li>
                  <li>To improve our website and services</li>
                  <li>To comply with legal obligations and protect our rights</li>
                  <li>To analyze usage patterns and optimize user experience</li>
                </ul>
              </div>
            </div>

            {/* Data Storage */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-4">4. Data Storage and Security</h2>
              <div className="text-muted-foreground space-y-4">
                <p>
                  We implement appropriate technical and organizational measures to protect your personal 
                  information against unauthorized access, alteration, disclosure, or destruction.
                </p>
                <p>
                  Your data is stored on secure servers and we use industry-standard encryption for data 
                  transmission. However, no method of transmission over the Internet is 100% secure.
                </p>
                <p>
                  We retain your personal information only for as long as necessary to fulfill the purposes 
                  outlined in this policy, unless a longer retention period is required by law.
                </p>
              </div>
            </div>

            {/* Sharing Information */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-4">5. Sharing Your Information</h2>
              <div className="text-muted-foreground space-y-4">
                <p>We may share your information with:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong className="text-foreground">Service Providers:</strong> Third-party vendors who assist in operating our website and services</li>
                  <li><strong className="text-foreground">Business Partners:</strong> Companies we collaborate with for joint offerings</li>
                  <li><strong className="text-foreground">Legal Requirements:</strong> When required by law or to protect our rights</li>
                  <li><strong className="text-foreground">Business Transfers:</strong> In connection with mergers, acquisitions, or asset sales</li>
                </ul>
                <p className="mt-4">
                  We do not sell your personal information to third parties.
                </p>
              </div>
            </div>

            {/* Your Rights */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-4">6. Your Rights</h2>
              <div className="text-muted-foreground space-y-4">
                <p>You have the right to:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Access the personal information we hold about you</li>
                  <li>Request correction of inaccurate information</li>
                  <li>Request deletion of your personal information</li>
                  <li>Object to or restrict processing of your information</li>
                  <li>Withdraw consent at any time</li>
                  <li>Request data portability</li>
                </ul>
                <p className="mt-4">
                  To exercise these rights, please contact us at <a href="mailto:info@sdcgulf.com" className="text-primary hover:underline">info@sdcgulf.com</a>.
                </p>
              </div>
            </div>

            {/* Cookies */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-4">7. Cookies and Tracking</h2>
              <div className="text-muted-foreground space-y-4">
                <p>
                  We use cookies and similar tracking technologies to track activity on our website and 
                  store certain information. Cookies are files with a small amount of data that may include 
                  an anonymous unique identifier.
                </p>
                <p>
                  You can instruct your browser to refuse all cookies or to indicate when a cookie is being 
                  sent. However, some features of our website may not function properly without cookies.
                </p>
              </div>
            </div>

            {/* Contact Information */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-4">8. Contact Us</h2>
              <div className="text-muted-foreground space-y-4">
                <p>
                  If you have any questions about this Privacy Policy, please contact us:
                </p>
                <ul className="list-none space-y-2">
                  <li><strong className="text-foreground">Email:</strong> info@sdcgulf.com</li>
                  <li><strong className="text-foreground">Phone:</strong> +966 5 737 505 48</li>
                  <li><strong className="text-foreground">Address:</strong> Jeddah, Kingdom of Saudi Arabia</li>
                </ul>
              </div>
            </div>

            {/* Changes */}
            <div className="mb-12">
              <h2 className="text-2xl font-bold text-foreground mb-4">9. Changes to This Policy</h2>
              <div className="text-muted-foreground space-y-4">
                <p>
                  We may update this Privacy Policy from time to time. We will notify you of any changes by 
                  posting the new Privacy Policy on this page and updating the "Last Updated" date.
                </p>
                <p>
                  We encourage you to review this Privacy Policy periodically for any changes.
                </p>
              </div>
            </div>

          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
